import { fabric } from "fabric";
import { qtyProxy } from "../../../../index";
import { IBrandingArea } from "../type";
import { initUndoRedoEventHandler, initAligningGuidelines } from "../util";
import { errorMessages } from "../../../assets/config";
import { updateCanvasWithAreaPosition } from "../util/printPositionDropDownController";
import { getImageBase64FromIdUrl, getCurrentCanvas, getDrawableArea, getImageDimensions, resizeImage } from "../../../helpers/helper";
import { fetchProductDataById } from "../../../helpers/apiHelper";

let canvas, drawableArea, editor;

/**
 * Handles the mouse zoom functionality for a specific canvas.
 * @returns {void} - The function does not return any value.
 */
export function mouseZoom() {
  let canvas = getCurrentCanvas();
  document.getElementById("zoomIn").addEventListener("click", () => {
    let zoom = canvas.getZoom();
    zoom *= 0.999 ** -300;
    if (zoom > 3) zoom = 3;
    if (zoom < 1) zoom = 1;
    canvas.zoomToPoint({ x: canvas.width / 2, y: canvas.height / 2 }, zoom);
  });

  document.getElementById("zoomOut").addEventListener("click", () => {
    let zoom = canvas.getZoom();
    zoom *= 0.999 ** 300;
    if (zoom > 20) zoom = 20;
    if (zoom < 1) zoom = 1;
    canvas.zoomToPoint({ x: canvas.width / 2, y: canvas.height / 2 }, zoom);
  });

  canvas.on("mouse:wheel", function (opt) {
    let delta = opt.e.deltaY;
    let zoom = canvas.getZoom();
    zoom *= 0.999 ** delta;
    if (zoom > 3) zoom = 3;
    if (zoom < 1) zoom = 1;
    canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
    opt.e.preventDefault();
    opt.e.stopPropagation();
  });

  let isPanning = false;
  let lastClientX;
  let lastClientY;

  canvas.on("mouse:down", function (event) {
    if (!canvas.getActiveObject()) {
      isPanning = true;
      canvas.defaultCursor = "grabbing"; // Change cursor to indicate panning
    }
    if (event.e.type === "touchstart" && event.e.touches?.[0]) {
      lastClientX = event.e.touches[0].clientX;
      lastClientY = event.e.touches[0].clientY;
    }
  });

  canvas.on("mouse:up", function () {
    isPanning = false;
    canvas.defaultCursor = "grab"; // Restore default cursor
  });

  canvas.on("mouse:move", function (event) {
    let clientX = event.e.movementX;
    let clientY = event.e.movementY;
    if (event.e.type === "touchmove" && event.e.touches) {
      clientX = lastClientX ? event.e.touches[0].clientX - lastClientX : 0;
      clientY = lastClientY ? event.e.touches[0].clientY - lastClientY : 0;
      lastClientX = event.e.touches[0].clientX;
      lastClientY = event.e.touches[0].clientY;
    }
    if (isPanning && event?.e) {
      let delta = new fabric.Point(clientX ?? 0, clientY ?? 0);
      canvas.relativePan(delta);
    }
  });
}

/**
 * Initializes the canvas for a selected branding area.
 *
 * @param {IBrandingArea} defaultSelectedBrandingArea - The selected branding area object that contains details such as dimensions and image data. This is the area where the canvas will be initialized.
 * @param {number} index - The index of the selected branding area. This be used for tracking which branding area is being initialized in a list of multiple areas.
 * @param {string} [editCanvasJson] - An optional string containing the canvas state in JSON format. If provided, this JSON data will be used to restore a previously edited canvas. Otherwise, the canvas will be initialized as new.
 *
 * @returns {Promise<void>} - The function is asynchronous and returns a `Promise` that resolves when the canvas has been fully initialized.
 */
export async function initCanvas(defaultSelectedBrandingArea: IBrandingArea, index: number, editCanvasJson?: string) {
  const canvasElement = document.getElementById(`productCanvas${index}`);
  const rootElement = document.getElementById("jasani_product_builder");
  const url = rootElement?.getAttribute("data-url");
  const isEdit = rootElement?.getAttribute("data-isEdit")?.toLocaleLowerCase() === "true";
  let overlayImgScaleX = 1;
  let overlayImgScaleY = 1;

  if (canvasElement) {
    canvasElement.style.display = "block"; // Ensure it's visible
  }
  const previousCanvas = qtyProxy["canvasListForPosition"]?.[index]?.canvas;

  // If the previous canvas exists than no need to create new one.
  if (previousCanvas) {
    return;
  }

  canvas = new fabric.Canvas(`productCanvas${index}`, {
    selection: false,
    controlsAboveOverlay: true,
    // @ts-ignore
    uniScaleKey: false,
  });

  // If `editCanvasJson` is provided, load the canvas from JSON
  if (isEdit && editCanvasJson) {
    await new Promise<void>((resolve) => {
      canvas.loadFromJSON(editCanvasJson, () => {
        canvas.getObjects().forEach((object) => {
          if (object.type === 'image') {
            object.setControlsVisibility({mt: false, ml: false, mr: false, mb: false})
          }
        })
        canvas.renderAll(); // Re-render the canvas once it has been loaded
        overlayImgScaleX = Number(canvas?.overlayImgScaleX ?? 1);
        overlayImgScaleY = Number(canvas?.overlayImgScaleY ?? 1);;
        resolve();
      });
    });
  }

  //Set the overlay image if edit mode is disabled.
  if (!isEdit) {
    const data = await fetchProductDataById(getImageBase64FromIdUrl(url, defaultSelectedBrandingArea.defaultImage_id));

    const size = await getImageDimensions(`data:image/jpeg;base64,${atob(data.defaultImage)}`);

    // Calculate the scale
    overlayImgScaleX = +(500 / size.width).toFixed(4);
    overlayImgScaleY = +(500 / size.height).toFixed(4);

    canvas.overlayImgScaleX = overlayImgScaleX; 
    canvas.overlayImgScaleY = overlayImgScaleY;

    const newResizedImage = await resizeImage(`data:image/jpeg;base64,${atob(data.defaultImage)}`, 500, 500);

    // Load the image as an overlay
    fabric.Image.fromURL(newResizedImage, function (img) {
      canvas.setOverlayImage(img, canvas.renderAll.bind(canvas), {
        selectable: false,
        globalCompositeOperation: "destination-atop",
      });
    });
  }

  fabric.Object.prototype.transparentCorners = false;
  
  // Created the drawable area.
  editor = new fabric.Rect({
    top: +defaultSelectedBrandingArea.top * overlayImgScaleY,
    left: +defaultSelectedBrandingArea.left * overlayImgScaleX,
    width: +defaultSelectedBrandingArea.width * overlayImgScaleX,
    height: +defaultSelectedBrandingArea.height * overlayImgScaleY,
  });

  drawableArea = new fabric.Rect({
    // @ts-ignore
    id: "drawableArea",
    top: +defaultSelectedBrandingArea.top * overlayImgScaleY,
    left: +defaultSelectedBrandingArea.left * overlayImgScaleX,
    width: +defaultSelectedBrandingArea.width * overlayImgScaleX ,
    height: +defaultSelectedBrandingArea.height * overlayImgScaleY,
    fill: "transparent",
    stroke: "red",
    strokeWidth: 1,
    strokeDashArray: [10],
    selectable: false,
  });

  if(!isEdit){
    canvas.clipPath = editor;
    editor.setCoords();
    drawableArea.setCoords();
    canvas.add(drawableArea);
  }
  canvas.renderAll();

  const initialHistory = {
    state: [
      {
        canvasJson: isEdit ? editCanvasJson : JSON.stringify(canvas.toJSON(["id", "selectable", "controlsVisibility", "libraryId","overlayImgScaleY","overlayImgScaleX"])),
        PreLogoList: [],
      },
    ],
    currentStateIndex: 0,
    undoStatus: false,
    redoStatus: false,
    undoFinishedStatus: true,
    redoFinishedStatus: true,
  };

  qtyProxy["initialHistory"] = initialHistory;

  updateCanvasWithAreaPosition(index, {
    ...qtyProxy["canvasListForPosition"][index],
    drawableArea,
    canvasEditor: editor,
    initialHistory,
    canvas,
  });
  initUndoRedoEventHandler();
  initAligningGuidelines();
  mouseZoom();
}

// Function to delete selected objects
export const deleteSelectedObjects = () => {
  const deleteButtons = document.querySelectorAll(".delete-buttons");

  deleteButtons?.forEach((btn) => {
    btn?.addEventListener("click", () => {
      const selectedObject = getCurrentCanvas().getActiveObject();
      const logoListCopy = JSON.parse(JSON.stringify(qtyProxy?.logoList || []));
      if (!selectedObject) {
        alert(errorMessages.ALERT_OBJECT_SELECTION);
        return;
      }
      const logoIndex = logoListCopy?.findIndex((el) => el.id == selectedObject.id);
      if (selectedObject && getCurrentCanvas() && confirm(errorMessages.CONFIRMATION_MESSAGE)) {
        if (logoIndex >= 0) {
          logoListCopy.splice(logoIndex, 1);
          qtyProxy["logoList"] = JSON.parse(JSON.stringify(logoListCopy));
        }
        getCurrentCanvas().remove(selectedObject);
        getCurrentCanvas().discardActiveObject();
        getCurrentCanvas().renderAll();
        getCurrentCanvas().fire("object:modified");
      }
    });
  })
};

function degrees_to_radians(degrees) {
  let pi = Math.PI;
  return degrees * (pi / 180);
}

/**
 * This function handles the alignment of active object related to print area, likely ensuring they are properly positioned
 * within their print area.
 * @returns {void} - The function does not return any value.
 */

export function alignToolsBtn() {
  const alignSelector = document.querySelectorAll(".align-tools-toolBtn");
  if (!alignSelector) return;
  alignSelector.forEach((el) => {
    el.addEventListener("click", () => {
      const activeObject = getCurrentCanvas().getActiveObject();
      const preAngle = activeObject.angle;
      let rotationalHeight;
      let rotationalWidth;
      let rotationalRight;
      let rotationalLeft;
      const sinHeight = (degree: number) => Math.sin(degrees_to_radians(degree)) * activeObject.getScaledHeight();
      const sinWidth = (degree: number) => Math.sin(degrees_to_radians(degree)) * activeObject.getScaledWidth();
      const cosHeight = (degree: number) => Math.cos(degrees_to_radians(degree)) * activeObject.getScaledHeight();
      const cosWidth = (degree: number) => Math.cos(degrees_to_radians(degree)) * activeObject.getScaledWidth();
      let tempAngle = preAngle;

      switch (true) {
        case preAngle <= 90: {
          const h1 = sinWidth(tempAngle);
          const h2 = sinHeight(90 - tempAngle);
          const w1 = cosWidth(tempAngle);
          const w2 = cosHeight(90 - tempAngle);
          rotationalHeight = h1 + h2;
          rotationalWidth = 0;
          rotationalRight = w1;
          rotationalLeft = w2;
          break;
        }

        case preAngle > 90 && preAngle <= 180: {
          tempAngle = tempAngle - 90;
          const h3 = sinHeight(tempAngle);
          const h4 = sinWidth(90 - tempAngle);
          const w3 = cosHeight(tempAngle);
          const w4 = cosWidth(90 - tempAngle);
          rotationalHeight = h4;
          rotationalWidth = h3;
          rotationalRight = 0;
          rotationalLeft = w3 + w4;
          break;
        }

        case preAngle > 180 && preAngle <= 270: {
          tempAngle = tempAngle - 180;
          const h5 = sinWidth(tempAngle);
          const h6 = sinHeight(90 - tempAngle);
          const w5 = cosWidth(tempAngle);
          const w6 = cosHeight(90 - tempAngle);
          rotationalHeight = 0;
          rotationalWidth = h5 + h6;
          rotationalRight = w6;
          rotationalLeft = w5;
          break;
        }

        default: {
          tempAngle = tempAngle - 270;
          const h7 = sinHeight(tempAngle);
          const h8 = sinWidth(90 - tempAngle);
          const w7 = cosHeight(tempAngle);
          const w8 = cosWidth(90 - tempAngle);
          rotationalHeight = h7;
          rotationalWidth = h8;
          rotationalRight = w7 + w8;
          rotationalLeft = 0;
          break;
        }
      }

      if (activeObject) {
        const { top: areaTop, left: areaLeft, width: areaWidth, height: areaHeight } = getDrawableArea();
        // @ts-ignore
        switch (el.value) {
          case "Top":
            activeObject.set("top", areaTop + rotationalWidth);
            break;
          case "Bottom":
            activeObject.set("top", areaTop + areaHeight - rotationalHeight);
            break;
          case "Left":
            activeObject.set("left", areaLeft + rotationalLeft);
            break;
          case "Right":
            activeObject.set("left", areaLeft + areaWidth - rotationalRight);
            break;
          case "Center":
            activeObject.rotate(0);
            activeObject.set("left", areaLeft + areaWidth / 2 - activeObject.getScaledWidth() / 2);
            activeObject.set("top", areaTop + areaHeight / 2 - activeObject.getScaledHeight() / 2);
            activeObject.rotate(preAngle);
            break;
          default:
            return;
        }
        getCurrentCanvas().fire("object:modified");
        getCurrentCanvas().renderAll();
      } else {
        alert(errorMessages.OBJ_NOT_SELECTED);
      }
    });
  });
}

export function backToOdooApp() {
  const rootElement = document.getElementById("jasani_product_builder");
  const backUrl = rootElement?.getAttribute("data-previous-url");

  if (backUrl) {
    const backButtonElement = document.getElementById("BackToOdoo");
    backButtonElement.addEventListener("click", () => {
      window.location.replace(backUrl);
    });
  }
}
